import React, { useState } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import Checkbox from '@mui/material/Checkbox'
import Paper from '@mui/material/Paper'
import Alert from '@mui/material/Alert'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'

/**
 * Component for managing panel layout sets within a panel array.
 * Used for calculating rails and roofing hooks based on panel arrangement.
 *
 * @param {array} layoutSets - Array of set objects
 * @param {number} totalPanels - Total panels in the array (numPanels)
 * @param {object} panelDimensions - { width_mm, height_mm } of selected panel
 * @param {function} onChange - Callback when sets change
 * @param {boolean} disabled - Whether editing is disabled
 */
/**
 * Configurable overage percentage for rail calculations
 * Formula: (total rail × (1 + OVERAGE%)) - waste = required overage
 * Change this value to adjust the overage percentage (e.g., 0.15 = 15%, 0.10 = 10%, 0.08 = 8%)
 */
const RAIL_OVERAGE_PERCENT = 0.15

const PanelLayoutSets = ({
  layoutSets = [],
  totalPanels = 0,
  panelDimensions = { width_mm: 1134, height_mm: 1722 },
  onChange,
  disabled = false,
  arrayNumber = 1
}) => {
  const [expanded, setExpanded] = useState(false)

  // Calculate total panels across all sets
  const setsTotal = layoutSets.reduce((sum, set) => {
    return sum + (set.rows || []).reduce((rowSum, count) => rowSum + (count || 0), 0)
  }, 0)

  const isValid = setsTotal === totalPanels || layoutSets.length === 0
  const hasSets = layoutSets.length > 0

  // Generate unique ID for new sets
  const getNextId = () => {
    const maxId = layoutSets.reduce((max, set) => Math.max(max, set.id || 0), 0)
    return maxId + 1
  }

  // Add a new empty set
  const handleAddSet = () => {
    const newSet = {
      id: getNextId(),
      description: `Set ${layoutSets.length + 1}`,
      panelOrientation: 'portrait',
      rowCount: 1,
      uniformRows: true,
      panelsPerRow: 1,
      rows: [1]
    }
    onChange([...layoutSets, newSet])
  }

  // Remove a set
  const handleRemoveSet = (setId) => {
    onChange(layoutSets.filter(set => set.id !== setId))
  }

  // Update a specific set
  const handleUpdateSet = (setId, updates) => {
    onChange(layoutSets.map(set => {
      if (set.id !== setId) return set

      const updatedSet = { ...set, ...updates }

      // If rowCount changed, adjust rows array
      if (updates.rowCount !== undefined) {
        const newRowCount = updates.rowCount
        const currentRows = set.rows || []

        if (newRowCount > currentRows.length) {
          // Add rows with default value
          const defaultValue = set.uniformRows ? (set.panelsPerRow || 1) : 1
          updatedSet.rows = [
            ...currentRows,
            ...Array(newRowCount - currentRows.length).fill(defaultValue)
          ]
        } else {
          // Trim rows
          updatedSet.rows = currentRows.slice(0, newRowCount)
        }
      }

      // If uniformRows toggled ON, fill all rows with panelsPerRow
      if (updates.uniformRows === true) {
        const value = updates.panelsPerRow || set.panelsPerRow || 1
        updatedSet.rows = Array(set.rowCount).fill(value)
        updatedSet.panelsPerRow = value
      }

      // If panelsPerRow changed and uniformRows is true, update all rows
      if (updates.panelsPerRow !== undefined && (set.uniformRows || updates.uniformRows)) {
        updatedSet.rows = Array(set.rowCount).fill(updates.panelsPerRow)
      }

      return updatedSet
    }))
  }

  // Update a specific row's panel count
  const handleUpdateRow = (setId, rowIndex, value) => {
    onChange(layoutSets.map(set => {
      if (set.id !== setId) return set
      const newRows = [...(set.rows || [])]
      newRows[rowIndex] = value
      return { ...set, rows: newRows }
    }))
  }

  // Calculate optimal rail pieces from 4m and 3m lengths
  const calculateRailPieces = (totalLengthMeters) => {
    if (totalLengthMeters <= 0) return { rail4m: 0, rail3m: 0, waste: 0 }

    // Try different combinations to minimize waste
    let bestCombo = { rail4m: 0, rail3m: 0, waste: Infinity }

    // Max possible rails needed
    const maxRails = Math.ceil(totalLengthMeters / 3) + 2

    for (let num4m = 0; num4m <= maxRails; num4m++) {
      for (let num3m = 0; num3m <= maxRails; num3m++) {
        const totalAvailable = (num4m * 4) + (num3m * 3)
        if (totalAvailable >= totalLengthMeters) {
          const waste = totalAvailable - totalLengthMeters
          // Prefer fewer total pieces, then less waste
          const currentTotal = num4m + num3m
          const bestTotal = bestCombo.rail4m + bestCombo.rail3m
          if (waste < bestCombo.waste || (waste === bestCombo.waste && currentTotal < bestTotal)) {
            bestCombo = { rail4m: num4m, rail3m: num3m, waste: Math.round(waste * 100) / 100 }
          }
        }
      }
    }

    return bestCombo
  }

  // Calculate rails, hooks, clamps, and end stops for a set
  const calculateRailsHooks = (set) => {
    if (!set.rows || set.rows.length === 0) {
      return { rails: 0, hooks: 0, railLength: 0, clamps: 0, endStops: 0, rail4m: 0, rail3m: 0, waste: 0 }
    }

    const railsPerRow = 2 // Top and bottom rails per row
    const hookSpacing = 1.2 // meters

    // Panel dimension based on orientation (convert mm to meters)
    const panelSize = set.panelOrientation === 'portrait'
      ? (panelDimensions.width_mm || 1134) / 1000
      : (panelDimensions.height_mm || 1722) / 1000

    // Calculate total rail length needed for all rows
    let totalRailLength = 0
    let totalClamps = 0

    set.rows.forEach(panelsInRow => {
      const rowRailLength = panelsInRow * panelSize
      totalRailLength += rowRailLength * 2 // Top and bottom rail for each row

      // Clamps: (N × 2) + 2 per row (mid clamps + end clamps)
      // Same clips are used for both mid and end clamps
      totalClamps += (panelsInRow * 2) + 2
    })

    const totalRails = set.rowCount * railsPerRow

    // Calculate hooks based on total rail length
    const avgRailLength = totalRailLength / totalRails
    const hooksPerRail = Math.ceil(avgRailLength / hookSpacing) + 1
    const totalHooks = hooksPerRail * totalRails

    // Calculate end stops (2 per rail - one at each end)
    const totalEndStops = totalRails * 2

    // Calculate optimal rail pieces
    const railPieces = calculateRailPieces(totalRailLength)

    return {
      rails: totalRails,
      hooks: totalHooks,
      railLength: totalRailLength.toFixed(2),
      clamps: totalClamps,
      endStops: totalEndStops,
      rail4m: railPieces.rail4m,
      rail3m: railPieces.rail3m,
      waste: railPieces.waste
    }
  }

  // Calculate totals across all sets
  const calculateTotals = () => {
    const baseTotals = layoutSets.reduce((totals, set) => {
      const setCalc = calculateRailsHooks(set)
      return {
        rails: totals.rails + setCalc.rails,
        hooks: totals.hooks + setCalc.hooks,
        clamps: totals.clamps + setCalc.clamps,
        endStops: totals.endStops + setCalc.endStops,
        rail4m: totals.rail4m + setCalc.rail4m,
        rail3m: totals.rail3m + setCalc.rail3m,
        waste: Math.round((totals.waste + setCalc.waste) * 100) / 100,
        railLength: Math.round((parseFloat(totals.railLength) + parseFloat(setCalc.railLength)) * 100) / 100
      }
    }, { rails: 0, hooks: 0, clamps: 0, endStops: 0, rail4m: 0, rail3m: 0, waste: 0, railLength: 0 })

    // Calculate overage: (total rail × (1 + OVERAGE%)) - waste = required overage
    const overageMultiplier = 1 + RAIL_OVERAGE_PERCENT
    const railWithOverage = Math.round(baseTotals.railLength * overageMultiplier * 100) / 100
    const requiredOverage = Math.round((railWithOverage - baseTotals.railLength - baseTotals.waste) * 100) / 100
    const overagePercentDisplay = Math.round(RAIL_OVERAGE_PERCENT * 100)

    return {
      ...baseTotals,
      railWithOverage,
      requiredOverage: Math.max(0, requiredOverage), // Don't show negative overage
      overagePercent: overagePercentDisplay
    }
  }

  const totals = calculateTotals()

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ mt: 2, mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
          <Typography sx={{ flexShrink: 0 }}>
            Panel Layout (Rails/Hooks)
          </Typography>
          <Chip
            label={`${layoutSets.length} set${layoutSets.length !== 1 ? 's' : ''}`}
            size="small"
            color={hasSets ? (isValid ? 'success' : 'warning') : 'default'}
          />
          {hasSets && (
            <>
              <Chip
                label={`${totals.rail4m}×4m + ${totals.rail3m}×3m rail`}
                size="small"
                variant="outlined"
                color="primary"
              />
              <Chip
                label={`+${totals.requiredOverage}m overage`}
                size="small"
                variant="outlined"
                color="success"
              />
              <Chip
                label={`${totals.clamps} mid/end clamps`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${totals.hooks} hooks`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${totals.endStops} end stops`}
                size="small"
                variant="outlined"
              />
            </>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {/* Validation summary */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2">
              Total Panels: <strong>{totalPanels}</strong>
              {hasSets && (
                <> | Sets Total: <strong style={{ color: isValid ? 'green' : 'orange' }}>{setsTotal}</strong></>
              )}
            </Typography>
            {!isValid && (
              <Alert severity="warning" sx={{ py: 0, px: 1 }}>
                Sets total ({setsTotal}) {setsTotal > totalPanels ? 'exceeds' : 'less than'} array total ({totalPanels})
              </Alert>
            )}
          </Box>
          {hasSets && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption">
                  <strong>Total Rail:</strong> {totals.railLength}m
                </Typography>
                <Typography variant="caption">
                  <strong>Rail Pieces:</strong> {totals.rail4m}×4m + {totals.rail3m}×3m
                </Typography>
                <Typography variant="caption">
                  <strong>Mid/End Clamps:</strong> {totals.clamps}
                </Typography>
                <Typography variant="caption">
                  <strong>Hooks:</strong> {totals.hooks}
                </Typography>
                <Typography variant="caption">
                  <strong>End Stops:</strong> {totals.endStops}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: '#e3f2fd', p: 0.5, borderRadius: 1 }}>
                <Typography variant="caption">
                  <strong>With {totals.overagePercent}% Overage:</strong> {totals.railWithOverage}m
                </Typography>
                <Typography variant="caption" color="warning.main">
                  <strong>Waste:</strong> {totals.waste}m
                </Typography>
                <Typography variant="caption" color="success.main">
                  <strong>Required Overage:</strong> {totals.requiredOverage}m
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Sets list */}
        {layoutSets.map((set, index) => (
          <SetEditor
            key={set.id}
            set={set}
            index={index}
            arrayNumber={arrayNumber}
            onUpdate={(updates) => handleUpdateSet(set.id, updates)}
            onUpdateRow={(rowIndex, value) => handleUpdateRow(set.id, rowIndex, value)}
            onRemove={() => handleRemoveSet(set.id)}
            calculations={calculateRailsHooks(set)}
            disabled={disabled}
          />
        ))}

        {/* Add set button */}
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddSet}
          variant="outlined"
          size="small"
          disabled={disabled}
          sx={{ mt: 1 }}
        >
          Add Set
        </Button>
      </AccordionDetails>
    </Accordion>
  )
}

/**
 * Editor component for a single layout set
 */
const SetEditor = ({
  set,
  index,
  arrayNumber,
  onUpdate,
  onUpdateRow,
  onRemove,
  calculations,
  disabled
}) => {
  const setPanelCount = (set.rows || []).reduce((sum, count) => sum + (count || 0), 0)

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="subtitle2" color="primary">
          Array {arrayNumber} - Set {index + 1}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={`${setPanelCount} panels`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${calculations.rail4m}×4m + ${calculations.rail3m}×3m`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${calculations.clamps} mid/end clamps`}
            size="small"
            color="info"
          />
          <Chip
            label={`${calculations.hooks} hooks`}
            size="small"
            color="secondary"
          />
          <Chip
            label={`${calculations.endStops} end stops`}
            size="small"
            variant="outlined"
          />
          <IconButton
            size="small"
            onClick={onRemove}
            disabled={disabled}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Description */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Description"
            value={set.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            size="small"
            fullWidth
            disabled={disabled}
            placeholder="e.g., Left of chimney"
          />
        </Grid>

        {/* Panel Orientation */}
        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset" size="small">
            <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>Panel Orientation</FormLabel>
            <RadioGroup
              row
              value={set.panelOrientation || 'portrait'}
              onChange={(e) => onUpdate({ panelOrientation: e.target.value })}
            >
              <FormControlLabel
                value="portrait"
                control={<Radio size="small" />}
                label="Portrait"
                disabled={disabled}
              />
              <FormControlLabel
                value="landscape"
                control={<Radio size="small" />}
                label="Landscape"
                disabled={disabled}
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Number of rows */}
        <Grid item xs={6} sm={3}>
          <TextField
            label="Rows"
            type="number"
            value={set.rowCount || 1}
            onChange={(e) => onUpdate({ rowCount: Math.max(1, parseInt(e.target.value) || 1) })}
            size="small"
            fullWidth
            disabled={disabled}
            inputProps={{ min: 1, max: 20 }}
          />
        </Grid>

        {/* Uniform rows toggle */}
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!set.uniformRows}
                onChange={(e) => onUpdate({ uniformRows: e.target.checked })}
                size="small"
                disabled={disabled}
              />
            }
            label="Same per row"
          />
        </Grid>

        {/* Panels per row (if uniform) */}
        {set.uniformRows && (
          <Grid item xs={12} sm={6}>
            <TextField
              label="Panels per row"
              type="number"
              value={set.panelsPerRow || 1}
              onChange={(e) => onUpdate({ panelsPerRow: Math.max(1, parseInt(e.target.value) || 1) })}
              size="small"
              fullWidth
              disabled={disabled}
              inputProps={{ min: 1, max: 50 }}
            />
          </Grid>
        )}

        {/* Individual row inputs (if not uniform) */}
        {!set.uniformRows && (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
              Panels in each row:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(set.rows || []).map((rowCount, rowIndex) => (
                <TextField
                  key={rowIndex}
                  label={`Row ${rowIndex + 1}`}
                  type="number"
                  value={rowCount || 0}
                  onChange={(e) => onUpdateRow(rowIndex, Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                  disabled={disabled}
                  inputProps={{ min: 0, max: 50 }}
                  sx={{ width: 80 }}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Rail/clamp info */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: '#fafafa', p: 1, borderRadius: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Total rail: <strong>{calculations.railLength}m</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Rail pieces: <strong>{calculations.rail4m}×4m + {calculations.rail3m}×3m</strong>
            </Typography>
            <Typography variant="caption" color={calculations.waste > 0.5 ? 'warning.main' : 'textSecondary'}>
              Waste: <strong>{calculations.waste}m</strong>
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default PanelLayoutSets
